import { useState } from "react";
import { liveFeed } from '@/mocks/homeData';

const zoneColor: Record<string, string> = {
  '现场': 'bg-primary-100 text-primary-700 border-primary-200',
  '快讯': 'bg-accent-100 text-accent-800 border-accent-200',
  '澄清': 'bg-secondary-200 text-secondary-900 border-secondary-300',
  '辟谣': 'bg-secondary-100 text-secondary-800 border-secondary-200',
};

const iconMap: Record<string, string> = {
  '现场': 'ri-camera-lens-line',
  '快讯': 'ri-flashlight-line',
  '澄清': 'ri-article-line',
  '辟谣': 'ri-shield-check-line',
};

export default function LiveTimeline() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <section id="live" className="relative overflow-hidden bg-background-50 py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          {/* timeline column */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <p className="inline-flex items-center gap-1.5 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900">
                  <i className="ri-time-line"></i>实时快讯流
                </p>
                <h2 className="mt-3 font-display text-2xl font-bold text-foreground-950 md:text-[28px]">吃瓜现场时间线</h2>
              </div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-600 whitespace-nowrap">
                <span className="h-2 w-2 animate-pulse rounded-full bg-primary-500"></span>
                7 条进行中
              </span>
            </div>

            <div className="relative mt-6 space-y-0 border-l-2 border-secondary-200 pl-5 ml-2">
              {liveFeed.map((item, i) => {
                const open = openIdx === i;
                return (
                  <div key={`${item.time}-${i}`} className="relative pb-6 last:pb-0">
                    {/* node dot */}
                    <span
                      className={`absolute -left-[27px] top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                        item.hot ? 'border-primary-400 bg-primary-100' : 'border-secondary-300 bg-background-50'
                      }`}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${item.hot ? 'bg-primary-500' : 'bg-secondary-400'}`}></span>
                    </span>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`font-display text-xs font-semibold ${item.hot ? 'text-primary-600' : 'text-secondary-500'}`}>{item.time}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${zoneColor[item.zone] ?? 'bg-secondary-100 text-secondary-800'}`}>
                        <i className={iconMap[item.zone] ?? 'ri-flashlight-line'}></i>
                        {item.zone}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-secondary-500">
                        <i className="ri-eye-line"></i>{item.reads} 已看
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setOpenIdx(open ? null : i)}
                      className="mt-1.5 block w-full cursor-pointer text-left"
                    >
                      <span className="text-sm font-semibold text-foreground-900 hover:text-primary-700 transition-colors">{item.title}</span>
                    </button>

                    {open && (
                      <div className="mt-2 rounded-lg border border-background-200 bg-background-100/80 px-3.5 py-3">
                        <p className="text-xs leading-relaxed text-foreground-700">
                          前排持续跟进中：现场目击者陆续补充细节，源信对比已贴出，瓜条完整度仍在增长，建议关注后续澄清与当事人回应。
                        </p>
                        <div className="mt-2.5 flex items-center gap-3">
                          <a href="#live" className="inline-flex items-center gap-1 text-xs font-semibold text-primary-600 hover:text-primary-700">
                            <i className="ri-external-link-line"></i>循原始信源
                          </a>
                          <span className="text-xs text-secondary-400">·</span>
                          <a href="#faq" className="inline-flex items-center gap-1 text-xs font-semibold text-accent-700 hover:text-accent-800">
                            存疑？先看澄清说明
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <button type="button" className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-dashed border-secondary-300 bg-background-100 py-3 text-sm font-medium text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">
              <i className="ri-loader-4-line"></i>下拉加载更早的吃瓜快讯
            </button>
          </div>

          {/* right trust / live helper */}
          <aside className="lg:pt-14">
            <div className="sticky top-24 space-y-4">
              <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50"><i className="ri-notification-3-line"></i></span>
                  <h3 className="font-display text-base font-bold text-foreground-950">开瓜提醒</h3>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                  关注你感兴趣的方向，重大瓜条发酵时第一时间推你前排。目前为浏览提醒演示，无需登录即可切换关注频道。
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {['娱乐现场', '综艺', '饭圈', '电竞', '红毯', '社会热闻'].map((t) => (
                    <button key={t} type="button" className="cursor-pointer whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-[11px] font-medium text-foreground-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 transition-colors">
                      + {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-primary-200 bg-primary-50 p-5">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50"><i className="ri-vip-crown-fill"></i></span>
                  <h3 className="font-display text-base font-bold text-foreground-950">前排围观位</h3>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-foreground-700">抢占吃瓜现场第一排，第一时间看到新增证据、反转声明与评论区风向，不错过任何一个名场面。</p>
                <a href="#top" className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors">
                  占个前排 <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}